// The ONE place that decides what a click, a drag or a Transformer may touch on
// a scene board. Two audiences with genuinely different semantics share the
// renderer, and the difference has to live in a single derived object rather
// than in a `admin ? ... : ...` at each call site:
//
//   END USER  — fills a template. Only `editable` elements are live; a
//               cover-fill collage cell PANS instead of moving; clicking an
//               image opens the asset gallery; a full-board frame stacked over
//               the cells is inert so clicks fall through to them.
//   ADMIN     — owns the design. Every FIELD-BOUND element is live, locked
//               fields included; nothing pans (the box itself is being moved);
//               clicking selects; a bound full-board element must stay
//               selectable even though it occludes.
//
// Scattering those nine decisions as independent `admin ?` checks is how one
// gets missed — the pan family alone spans Transformer attachment, the drag
// bound function, the pinch listeners and the image click route.

import { isUserLayerId, KonvaNode } from './sceneToKonva';
import { TextEditMeta } from './SceneRenderer';

export interface InteractionPolicy {
    readonly admin: boolean;
    /** May this node be selected, dragged and transformed at all? */
    selectable(node: KonvaNode): boolean;
    /** Should this node ignore pointer events entirely (clicks fall through)? */
    inert(node: KonvaNode): boolean;
    /** Does a drag PAN the image inside a fixed clip instead of moving the box?
     *  Gates the whole pan family — Transformer attachment included. */
    pannable(node: KonvaNode): boolean;
    /** Does a click open the parent's asset gallery rather than select? */
    imageClickOpensGallery(node: KonvaNode): boolean;
    /** Dashed discoverability outline for a text whose VALUE can be edited.
     *  It advertises the gesture that opens the editor — a single click for the
     *  end user, a double-click (or the page's button / Enter) for the admin —
     *  so it must track `inlineTextEditable`, not mere selectability. */
    textHint(node: KonvaNode): boolean;
    /** May the inline text editor be opened on this node at all? END USER: a
     *  single click does it. ADMIN: a click only selects — the editor opens on
     *  double-click, on Enter, or through SceneEditorHandle.enterTextEdit. */
    inlineTextEditable(node: KonvaNode): boolean;
    /** Rotation handles. Off in admin: the Figma extractor emits rotated nodes
     *  as post-rotation AABBs with rotation=0, so an admin rotation delta could
     *  not compose with a Figma-side rotation change on the next re-sync. */
    readonly rotateEnabled: boolean;
    /** Rotation handles for THIS node. `rotateEnabled` is the mode-level answer;
     *  a user TEXT layer additionally refuses it, because the inline editor has
     *  no rotated form (inlineTextEditable requires |rotation| < 0.01) and a
     *  rotated layer would be permanently uneditable. */
    rotatable(node: KonvaNode): boolean;
}

interface PolicyInput {
    admin: boolean;
    /** Ids flagged by isOccludingOverlay — a full-board image over smaller cells. */
    occludingIds: Set<string>;
    textMeta?: (inputKey: string) => TextEditMeta | null;
}

/** Unrotated is a precondition for both the pan drag math and the inline text
 *  overlay; neither has a rotated form. */
const unrotated = (node: KonvaNode): boolean => {
    const rotation = node.props.rotation;
    return Math.abs(typeof rotation === 'number' ? rotation : 0) < 0.01;
};

const hasImagePan = (node: KonvaNode): boolean =>
    node.konvaType === 'Image' && !!(node.props as Record<string, unknown>)._imagePan;

export const interactionPolicy = ({admin, occludingIds, textMeta}: PolicyInput): InteractionPolicy => {
    // ADMIN: bound + identified. `editable` is deliberately ignored — a locked
    // field is still admin-owned design. No sourceNodeId means the edit could
    // not be recorded against a Figma node, so it would break the "baked scene
    // == Figma scene + records" invariant and die silently on the next re-sync;
    // such an element is not selectable in either editor.
    const adminSelectable = (node: KonvaNode): boolean =>
        !!node.elementId && !!node.inputKey && !!node.sourceNodeId;

    const endUserSelectable = (node: KonvaNode): boolean =>
        !!node.elementId && !!node.editable && !occludingIds.has(node.elementId);

    const selectable = admin ? adminSelectable : endUserSelectable;

    /** A text whose VALUE the inline editor may open. Shared by textHint and
     *  inlineTextEditable so the outline and the gesture cannot disagree. */
    const selectableText = (node: KonvaNode): boolean => (
        node.konvaType === 'Text'
        && !!node.elementId
        && !!node.inputKey
        && selectable(node)
        && !!textMeta?.(node.inputKey)?.editable
        && unrotated(node)
    );

    return {
        admin,
        selectable,
        inert: (node: KonvaNode): boolean => {
            if (!node.elementId || !occludingIds.has(node.elementId)) return false;
            // A bound full-board element (a background photo field) is a real
            // admin target; only the unbound decorative frame stays inert.
            return !selectable(node);
        },
        pannable: (node: KonvaNode): boolean => (
            admin ? false : hasImagePan(node) && unrotated(node)
        ),
        imageClickOpensGallery: (node: KonvaNode): boolean => (
            !admin && node.konvaType === 'Image' && !!node.inputKey
        ),
        textHint: (node: KonvaNode): boolean => {
            if (node.konvaType !== 'Text' || !node.elementId) return false;
            // ADMIN: hint the texts whose value can actually be OPENED, not every
            // selectable one. It used to mark all of them, which was honest while
            // a click opened the editor; now that a click only selects, marking a
            // text with no editable value would advertise a gesture that does
            // nothing.
            if (admin) return selectableText(node);
            if (!node.editable) return false;
            return !(node.inputKey && textMeta && !textMeta(node.inputKey)?.editable);
        },
        inlineTextEditable: selectableText,
        rotateEnabled: !admin,
        rotatable: (node: KonvaNode): boolean => (
            !admin && !(node.konvaType === 'Text' && isUserLayerId(node.elementId))
        ),
    };
};
